/**
 * Helpers for the optional showcase access code.
 *
 * This is a light gate (checked in the visitor's browser), designed to keep
 * casual visitors out of a public showcase page — not strong security.
 */

const STORAGE_PREFIX = 'showcase_access_';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// Unambiguous characters only (no O/0, I/1, etc.)
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export const generateShowcaseAccessCode = (length = 6): string => {
  let code = '';
  const random = new Uint32Array(length);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(random);
    for (let i = 0; i < length; i++) {
      code += CODE_ALPHABET[random[i] % CODE_ALPHABET.length];
    }
    return code;
  }
  for (let i = 0; i < length; i++) {
    code += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)];
  }
  return code;
};

export const normalizeShowcaseAccessCode = (value: string | null | undefined): string =>
  (value || '').trim().toUpperCase();

export const showcaseCodesMatch = (
  entered: string | null | undefined,
  expected: string | null | undefined
): boolean => {
  const a = normalizeShowcaseAccessCode(entered);
  const b = normalizeShowcaseAccessCode(expected);
  return a.length > 0 && a === b;
};

interface StoredAccess {
  code: string;
  expiresAt: number;
}

const storageKey = (organizationId: string) => `${STORAGE_PREFIX}${organizationId}`;

/** Returns true when this device already unlocked the showcase with the current code. */
export const hasStoredShowcaseAccess = (
  organizationId: string,
  expectedCode: string | null | undefined
): boolean => {
  if (!organizationId || !expectedCode) return false;
  try {
    const raw = localStorage.getItem(storageKey(organizationId));
    if (!raw) return false;
    const parsed = JSON.parse(raw) as StoredAccess;
    if (!parsed?.code || !parsed?.expiresAt) return false;
    if (Date.now() > parsed.expiresAt) {
      localStorage.removeItem(storageKey(organizationId));
      return false;
    }
    return showcaseCodesMatch(parsed.code, expectedCode);
  } catch {
    return false;
  }
};

/** Remembers a successful unlock on this device for 30 days. */
export const storeShowcaseAccess = (organizationId: string, code: string): void => {
  if (!organizationId) return;
  try {
    const payload: StoredAccess = {
      code: normalizeShowcaseAccessCode(code),
      expiresAt: Date.now() + THIRTY_DAYS_MS,
    };
    localStorage.setItem(storageKey(organizationId), JSON.stringify(payload));
  } catch {
    // Storage unavailable (private browsing) — the visitor just re-enters the code.
  }
};
